import Axios, {
  AxiosRequestTransformer,
  AxiosResponse,
  AxiosResponseTransformer,
  InternalAxiosRequestConfig,
} from "axios";

import {
  convertDateToStringYYYYMMDD,
  ERROR_ACCESS_DENIED,
  ERROR_APPLICATION,
  ERROR_NO_RECORD_FOUND,
  ERROR_NO_ROWS_UPDATED,
  ERROR_REQUEST_TIMEOUT,
  jsonReviver,
} from "../utils";
import { recursivelyRemoveEmptyStrings } from "../utils/rest";
import { getLogonRoute } from "../utils/routes";
import { Toast } from "../widget";
import { logError } from "./error";
import { memoizedRefreshAccessToken } from "./identity";
import { ServiceError, Status } from "./Shared";
import { getErrorMessageFromCode } from "./ValidationErrorFormatter";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const responseTransformer: AxiosResponseTransformer = (data: any): any => {
  if (data !== undefined && data !== null && typeof data === "string" && data !== "")
    return JSON.parse(data, jsonReviver);
  return data;
};

export const dateTransformer = (data: unknown): unknown => {
  if (data instanceof Date) {
    return data.toISOString().replace(/.\d\d\dZ$/, "Z");
  }
  if (Array.isArray(data)) {
    return data.map(dateTransformer);
  }
  if (typeof data === "object" && data !== null) {
    return Object.fromEntries(Object.entries(data).map(([key, value]) => [key, dateTransformer(value)]));
  }
  return data;
};

export const dateOnlyTransformer = (data: unknown): unknown => {
  if (data instanceof Date) {
    return convertDateToStringYYYYMMDD(data);
  }
  if (Array.isArray(data)) {
    return data.map(dateTransformer);
  }
  if (typeof data === "object" && data !== null) {
    return Object.fromEntries(Object.entries(data).map(([key, value]) => [key, dateTransformer(value)]));
  }
  return data;
};

const requestTransformer: AxiosRequestTransformer = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  return recursivelyRemoveEmptyStrings(data);
};

export const axios = Axios.create({
  baseURL: window.API_URL,
  timeout: 15000,
  transformRequest: [requestTransformer].concat(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Axios.defaults.transformRequest as any
  ),
  transformResponse: [responseTransformer].concat(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Axios.defaults.transformResponse as any
  ),
});

const requestHandler = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  // Generic request data modification (eg, dateTime handling)
  if (config.headers && window.ACCESS_TOKEN) {
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${window.ACCESS_TOKEN}`;
  }
  return config;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const errorRequestHandler = (error: any): Promise<any> => {
  // Generic error handling code
  Toast.error({ message: "The application has encountered an unknown error." });
  logError({ error });
  return Promise.reject(error);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const responseHandler = (response: AxiosResponse): any => {
  // Generic response data modification (eg, dateTime handling)
  return response;
};

// Axios reports its own timeout as ECONNABORTED (or ETIMEDOUT with `transitional.clarifyTimeoutError`)
// and never attaches a response - which is what tells it apart from a server that answered 5xx.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isTimeoutError = (error: any): boolean =>
  !error?.response && (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT");

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const errorResponseHandler = async (error: any): Promise<any> => {
  // Generic error handling code
  const config = error?.config;
  // Note response may be null if the connection timed out...
  const errors = error.response?.data?.errors as ServiceError[];
  errors?.forEach((e) => {
    const params: { [index: string]: string | undefined } = {
      ...e.parameters,
      property: e.property,
      path: e.path,
    };
    e.message = getErrorMessageFromCode(e.code, params);
  });

  // Check if is 401 Unauthorized error, refresh the access token and retry the query once
  if (error?.response?.status === 401 && !config?.sent) {
    config.sent = true;
    const result = await memoizedRefreshAccessToken();

    if (result.status === Status.Error) {
      // Redirect to login if not able to refresh token (e.g refresh token expired)
      window.location.href = getLogonRoute();
    }
    return axios(config);
  }
  if (error?.response?.status === 400) {
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({ errors });
  }
  if (error?.response?.status === 403) {
    Toast.error({
      message: getErrorMessageFromCode(ERROR_ACCESS_DENIED),
    });
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({ errors });
  }
  if (error?.response?.status === 404) {
    Toast.error({
      message: getErrorMessageFromCode(ERROR_NO_RECORD_FOUND),
    });
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({ errors });
  }
  if (error?.response?.status === 409) {
    Toast.warn({
      message: getErrorMessageFromCode(ERROR_NO_ROWS_UPDATED),
    });
    return Promise.reject(errors);
  }
  // Axios gave up waiting (the request's `timeout`): there is no response object, and the api may
  // still be working on the request. Handed to the caller as a structured error, under the same
  // `{ errors }` shape a 400 takes, so a page can tell "timed out" from "refused" and say so.
  if (isTimeoutError(error)) {
    const timeoutError: ServiceError = {
      code: ERROR_REQUEST_TIMEOUT,
      message: getErrorMessageFromCode(ERROR_REQUEST_TIMEOUT),
    };
    Toast.error({ message: timeoutError.message });
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({ errors: [timeoutError] });
  }
  // 500, or anything else
  Toast.error({
    message: getErrorMessageFromCode(ERROR_APPLICATION),
  });
  return Promise.reject(errors);
};

axios.interceptors.request.use(requestHandler, errorRequestHandler);
axios.interceptors.response.use(responseHandler, errorResponseHandler);
